/**
 * Estrutura da aplicação: trilho lateral no desktop/tablet e barra inferior no
 * celular. Itens aparecem conforme o papel (vendedora vê só o que usa).
 */
import type { Papel, StatusPresenca } from "@ydchat/shared";
import { ROTULO_PAPEL, ROTULO_PRESENCA, STATUS_PRESENCA, veOperacaoInteira } from "@ydchat/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useRouterState } from "@tanstack/react-router";
import {
  BarChart3,
  Keyboard,
  KeyRound,
  LogOut,
  Search,
  KanbanSquare,
  MessagesSquare,
  Settings,
  Sparkles,
  Users,
  Workflow,
  UserSquare2,
  type LucideIcon,
} from "lucide-react";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { COR_PRESENCA, useAlertas } from "@/lib/equipe";
import { cn } from "@/lib/cn";
import { chaveSessao, useUsuario } from "@/lib/sessao";
import type { EstadoConexao } from "@/lib/tempo-real";
import { Avatar } from "../ui/avatar";
import { PainelLateral } from "../ui/painel-lateral";
import { BuscaGlobal, useAtalhoBusca } from "./busca-global";
import { FormTrocarSenha } from "./trocar-senha";
import { ATALHOS, emitir, useAtalho, useAtalhosGlobais } from "@/lib/atalhos";

interface ItemNav {
  para: string;
  rotulo: string;
  icone: LucideIcon;
  papeis: Papel[];
}

const ITENS: ItemNav[] = [
  {
    para: "/atendimentos",
    rotulo: "Atendimentos",
    icone: MessagesSquare,
    papeis: ["admin", "gerencia", "vendedora"],
  },
  {
    para: "/funil",
    rotulo: "Funil",
    icone: KanbanSquare,
    papeis: ["admin", "gerencia", "vendedora"],
  },
  {
    para: "/clientes",
    rotulo: "Clientes",
    icone: UserSquare2,
    papeis: ["admin", "gerencia", "vendedora"],
  },
  { para: "/equipe", rotulo: "Equipe e fila", icone: Users, papeis: ["admin", "gerencia"] },
  { para: "/painel", rotulo: "Painel", icone: BarChart3, papeis: ["admin", "gerencia"] },
  { para: "/inteligencia", rotulo: "Inteligência", icone: Sparkles, papeis: ["admin", "gerencia"] },
  { para: "/fluxos", rotulo: "Fluxos", icone: Workflow, papeis: ["admin", "gerencia"] },
  {
    para: "/configuracoes",
    rotulo: "Configurações",
    icone: Settings,
    papeis: ["admin", "gerencia"],
  },
];

export function AppShell({ children, conexao }: { children: ReactNode; conexao: EstadoConexao }) {
  const usuario = useUsuario();
  const caminho = useRouterState({ select: (s) => s.location.pathname });
  const itens = ITENS.filter((i) => i.papeis.includes(usuario.papel));
  const alertas = useAlertas(veOperacaoInteira(usuario.papel));
  const comAlerta = (para: string) => para === "/equipe" && !!alertas.data?.length;
  // No celular, dentro de uma conversa, a barra inferior sai para dar espaço ao teclado.
  const emConversa = /^\/atendimentos\/[^/]+/.test(caminho);
  const [buscaAberta, setBuscaAberta] = useState(false);
  const abrirBusca = useCallback(() => setBuscaAberta(true), []);
  useAtalhoBusca(abrirBusca);
  useAtalhosGlobais();
  const [ajudaAberta, setAjudaAberta] = useState(false);
  useAtalho("ajuda", () => setAjudaAberta(true));
  useContadorNoTitulo();
  // Outras telas (ex.: cabeçalho da lista no celular) pedem a busca por evento.
  useEffect(() => {
    window.addEventListener("ydchat:abrir-busca", abrirBusca);
    return () => window.removeEventListener("ydchat:abrir-busca", abrirBusca);
  }, [abrirBusca]);

  return (
    <div className="flex h-dvh overflow-hidden bg-neutral-50">
      <aside className="hidden w-[76px] shrink-0 flex-col items-center border-r border-neutral-200 bg-white py-4 md:flex">
        <img src="/logo-yd-monogram.png" alt="YourDress" className="mb-6 size-9 object-contain" />
        <button
          onClick={abrirBusca}
          title="Buscar (Ctrl+K)"
          aria-label="Buscar"
          className="mb-3 flex size-11 items-center justify-center rounded-lg text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800"
        >
          <Search size={20} strokeWidth={1.75} />
        </button>
        <nav className="flex flex-1 flex-col items-center gap-1" aria-label="Principal">
          {itens.map((i) => {
            const ativo = caminho.startsWith(i.para);
            return (
              <Link
                key={i.para}
                to={i.para}
                title={i.rotulo}
                aria-label={i.rotulo}
                className={cn(
                  "group flex w-16 flex-col items-center gap-1 rounded-lg py-2 text-[10.5px] font-semibold transition-colors",
                  ativo
                    ? "bg-gold-50 text-gold-800"
                    : "text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800",
                )}
              >
                <span className="relative">
                  <i.icone size={20} strokeWidth={1.75} />
                  {comAlerta(i.para) && (
                    <span
                      aria-label="Há alertas"
                      className="absolute -right-1.5 -top-1 size-2.5 rounded-full bg-danger-500 ring-2 ring-white"
                    />
                  )}
                </span>
                <span className="leading-tight">{i.rotulo.split(" ")[0]}</span>
              </Link>
            );
          })}
        </nav>
        <IndicadorConexao estado={conexao} />
        <MenuUsuario />
      </aside>

      <main className="flex min-w-0 flex-1 flex-col">{children}</main>
      <BuscaGlobal aberta={buscaAberta} onFechar={() => setBuscaAberta(false)} />

      {!emConversa && (
        <nav
          aria-label="Principal"
          className="area-segura-inferior fixed inset-x-0 bottom-0 z-30 flex border-t border-neutral-200 bg-white/95 backdrop-blur-[10px] backdrop-saturate-[1.6] md:hidden"
        >
          {itens.slice(0, 4).map((i) => {
            const ativo = caminho.startsWith(i.para);
            return (
              <Link
                key={i.para}
                to={i.para}
                className={cn(
                  "flex min-h-14 flex-1 flex-col items-center justify-center gap-0.5 text-[11px] font-semibold",
                  ativo ? "text-gold-700" : "text-neutral-500",
                )}
              >
                <i.icone size={22} strokeWidth={1.75} />
                {i.rotulo.split(" ")[0]}
              </Link>
            );
          })}
          <MenuUsuario compacto />
        </nav>
      )}
      <PainelLateral
        aberto={ajudaAberta}
        onFechar={() => setAjudaAberta(false)}
        titulo="Atalhos de teclado"
      >
        <ul className="divide-y divide-neutral-100">
          {ATALHOS.map((a) => (
            <li key={a.acao} className="flex items-center justify-between gap-3 py-2.5 text-sm">
              <span className="text-neutral-700">{a.acao}</span>
              <span className="flex shrink-0 gap-1">
                {a.teclas.map((t) => (
                  <kbd
                    key={t}
                    className="rounded border border-neutral-300 bg-neutral-50 px-1.5 py-0.5 text-xs font-semibold text-neutral-700"
                  >
                    {t}
                  </kbd>
                ))}
              </span>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-neutral-500">
          No Mac, use Option no lugar de Alt e ⌘ no lugar de Ctrl.
        </p>
      </PainelLateral>
    </div>
  );
}

function IndicadorConexao({ estado }: { estado: EstadoConexao }) {
  if (estado === "online") return null;
  return (
    <span
      role="status"
      title={
        estado === "offline" ? "Sem conexão em tempo real. Tentando reconectar." : "Conectando"
      }
      className="mb-3 rounded-full bg-warning-100 px-2 py-0.5 text-[10px] font-semibold text-warning-500"
    >
      {estado === "offline" ? "Offline" : "..."}
    </span>
  );
}

function MenuUsuario({ compacto = false }: { compacto?: boolean }) {
  const usuario = useUsuario();
  const qc = useQueryClient();
  const [aberto, setAberto] = useState(false);
  const [trocandoSenha, setTrocandoSenha] = useState(false);

  const presenca = useMutation({
    mutationFn: (status: StatusPresenca) => api.patch("/usuarios/eu/presenca", { status }),
    onMutate: (status) => {
      qc.setQueryData(chaveSessao, (s: typeof usuario | null | undefined) =>
        s ? { ...s, statusPresenca: status } : s,
      );
    },
    onError: () => {
      toast.error("Não foi possível mudar seu status.");
      void qc.invalidateQueries({ queryKey: chaveSessao });
    },
  });
  const sair = useMutation({
    mutationFn: () => api.post("/auth/logout"),
    onSettled: () => {
      qc.clear();
      qc.setQueryData(chaveSessao, null);
    },
  });

  return (
    <div className={cn("relative", compacto ? "flex flex-1" : "")}>
      <button
        onClick={() => setAberto((a) => !a)}
        aria-expanded={aberto}
        aria-label={`${usuario.nome}, ${ROTULO_PRESENCA[usuario.statusPresenca]}`}
        className={cn(
          "relative flex items-center justify-center",
          compacto ? "min-h-14 flex-1" : "rounded-full p-1 hover:bg-neutral-100",
        )}
      >
        <Avatar nome={usuario.nome} tamanho={compacto ? "sm" : "md"} />
        <span
          className={cn(
            "absolute rounded-full ring-2 ring-white",
            compacto ? "bottom-3 right-[calc(50%-14px)] size-2.5" : "bottom-1 right-1 size-3",
            COR_PRESENCA[usuario.statusPresenca],
          )}
        />
      </button>
      {aberto && (
        <>
          <button
            aria-label="Fechar menu"
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setAberto(false)}
          />
          <div
            className={cn(
              "absolute z-50 w-60 rounded-xl border border-neutral-200 bg-white p-2 shadow-lg",
              compacto ? "bottom-[calc(100%+8px)] right-2" : "bottom-0 left-[calc(100%+8px)]",
            )}
          >
            <div className="px-2 pb-2 pt-1">
              <p className="font-semibold text-neutral-900">{usuario.nome}</p>
              <p className="text-xs text-neutral-500">{ROTULO_PAPEL[usuario.papel]}</p>
            </div>
            <div className="border-t border-neutral-200 py-1">
              {STATUS_PRESENCA.map((s) => (
                <button
                  key={s}
                  onClick={() => {
                    presenca.mutate(s);
                    setAberto(false);
                  }}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm hover:bg-neutral-100",
                    usuario.statusPresenca === s && "font-semibold text-neutral-900",
                  )}
                >
                  <span className={cn("size-2.5 rounded-full", COR_PRESENCA[s])} />
                  {ROTULO_PRESENCA[s]}
                </button>
              ))}
            </div>
            <div className="border-t border-neutral-200 pt-1">
              <button
                onClick={() => {
                  setTrocandoSenha(true);
                  setAberto(false);
                }}
                className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm text-neutral-700 hover:bg-neutral-100"
              >
                <KeyRound size={16} strokeWidth={1.75} /> Trocar senha
              </button>
              <button
                onClick={() => {
                  setAberto(false);
                  emitir("ajuda");
                }}
                className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm text-neutral-700 hover:bg-neutral-100 max-md:hidden"
              >
                <Keyboard size={16} strokeWidth={1.75} /> Atalhos de teclado
              </button>
              <button
                onClick={() => sair.mutate()}
                className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm text-neutral-700 hover:bg-neutral-100"
              >
                <LogOut size={16} strokeWidth={1.75} /> Sair
              </button>
            </div>
          </div>
        </>
      )}
      <PainelLateral
        aberto={trocandoSenha}
        onFechar={() => setTrocandoSenha(false)}
        titulo="Trocar senha"
      >
        <FormTrocarSenha onConcluido={() => setTrocandoSenha(false)} />
      </PainelLateral>
    </div>
  );
}

/** "(3) YourDress Chat": clientes esperando resposta nas minhas conversas. */
function useContadorNoTitulo() {
  const minhas = useQuery({
    queryKey: ["conversas", "titulo"],
    queryFn: () =>
      api.get<{ aguardandoRespostaDesde: string | null }[]>(
        "/conversas?escopo=minhas&status=abertas",
      ),
    refetchInterval: 60_000,
  });
  const n = minhas.data?.filter((c) => c.aguardandoRespostaDesde).length ?? 0;
  useEffect(() => {
    document.title = n ? `(${n}) YourDress Chat` : "YourDress Chat";
  }, [n]);
}
